import React, { useState, useEffect } from 'react';
import { fetchContactDetails, sendMessage } from '../../services/api';
import { FaEnvelope, FaPhone, FaMobileAlt, FaMapMarkerAlt, FaLocationArrow } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_number: '',
    message: ''
  });
  const [contactDetails, setContactDetails] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchContactDetails();
        if (data.contacts && data.contacts.length > 0) {
          setContactDetails(data.contacts[0]);
        }
      } catch (error) {
        console.error('Error fetching contact details:', error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await sendMessage(formData);
      if (response.success) {
        toast.success(response.message || 'Message sent successfully! We will get back to you soon.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          contact_number: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.message || 'Failed to send message. Please try again later.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="text-center">
        <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
          Contact Us
        </h1>
        <p className="mt-4 text-xl text-gray-500">
          Get in touch with us for any inquiries or support
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
        {contactDetails && (
          <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
            <div className="space-y-6">
              {contactDetails.email && (
                <div className="flex items-start space-x-4 group">
                  <div className="bg-[#1E4C9A] p-3 rounded-full group-hover:bg-[#1A8D32] transition-colors duration-200">
                    <FaEnvelope className="w-5 h-5 text-white group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Email</h3>
                    <a href={`mailto:${contactDetails.email}`} className="text-gray-600 hover:text-green-600 transition-colors duration-200">
                      {contactDetails.email}
                    </a>
                  </div>
                </div>
              )}
              {contactDetails.phone && (
                <div className="flex items-start space-x-4 group">
                  <div className="bg-[#1E4C9A] p-3 rounded-full group-hover:bg-[#1A8D32] transition-colors duration-200">
                    <FaPhone className="w-5 h-5 text-white group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Phone</h3>
                    <a href={`tel:${contactDetails.phone}`} className="text-gray-600 hover:text-green-600 transition-colors duration-200">
                      {contactDetails.phone}
                    </a>
                  </div>
                </div>
              )}
              {contactDetails.mobile && (
                <div className="flex items-start space-x-4 group">
                  <div className="bg-[#1E4C9A] p-3 rounded-full group-hover:bg-[#1A8D32] transition-colors duration-200">
                    <FaMobileAlt className="w-5 h-5 text-white group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Mobile</h3>
                    <a href={`tel:${contactDetails.mobile}`} className="text-gray-600 hover:text-green-600 transition-colors duration-200">
                      {contactDetails.mobile}
                    </a>
                  </div>
                </div>
              )}
              {contactDetails.address && (
                <div className="flex items-start space-x-4 group">
                  <div className="bg-[#1E4C9A] p-3 rounded-full group-hover:bg-[#1A8D32] transition-colors duration-200">
                    <FaMapMarkerAlt className="w-5 h-5 text-white group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Address</h3>
                    <p className="text-gray-600">{contactDetails.address}</p>
                  </div>
                </div>
              )}
              {contactDetails.location && (
                <div className="flex items-start space-x-4 group">
                  <div className="bg-[#1E4C9A] p-3 rounded-full group-hover:bg-[#1A8D32] transition-colors duration-200">
                    <FaLocationArrow className="w-5 h-5 text-white group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Location</h3>
                    <p className="text-gray-600">{contactDetails.location}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 transition-colors duration-200"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 transition-colors duration-200"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="contact_number" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                id="contact_number"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 transition-colors duration-200"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 transition-colors duration-200"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-[#1A8D32] text-white py-3 px-4 rounded-lg hover:bg-[#1E4C9A] transition-colors duration-200`}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </div>
                ) : (
                  'Send Message'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactPage; 